/**
 * Manage Teams - View and manage all teams across programs (branch-level)
 * Teams are branch-scoped resources
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Users, UserPlus, Copy, Check, CheckCircle, XCircle } from 'lucide-react';
import apiService from '../../services/api';
import { AdminPageLayout, Section } from '../../components';
import {
  Listing,
  ListingActionBar,
  ListingFilter,
  ListingItem
} from '../../components/listing/v2';
import {
  EditAction,
  DeleteAction,
  CreateAction
} from '../../components/listing/v2/ListingAction';
import ConfirmDialog from '../../components/ConfirmDialog';
import { useToast } from '../../contexts/ToastContext';

const ManageTeams = () => {
  const navigate = useNavigate();
  const { branch_id } = useParams();
  const { showSuccess, showError } = useToast();
  const [loading, setLoading] = useState(true);
  const [teams, setTeams] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [seasons, setSeasons] = useState([]);
  const [currentSeasonId, setCurrentSeasonId] = useState('');
  const [filters, setFilters] = useState({
    search: '',
    program: '',
    status: '',
    season: ''
  });
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [copiedTeamId, setCopiedTeamId] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const loadSeasons = useCallback(async () => {
    try {
      const response = await apiService.getSeasons({});
      const allSeasons = response.seasons || [];
      
      // Filter to current and future seasons only
      const today = new Date();
      const futureSeasons = allSeasons
        .filter(season => {
          const endDate = new Date(season.end_date);
          return endDate >= today;
        })
        .sort((a, b) => new Date(a.start_date) - new Date(b.start_date));
      
      setSeasons(futureSeasons);
      
      // Find and set current season as default
      const currentSeason = futureSeasons.find(season => {
        const startDate = new Date(season.start_date);
        const endDate = new Date(season.end_date);
        return startDate <= today && endDate >= today;
      });
      
      if (currentSeason) {
        setCurrentSeasonId(currentSeason.id);
        setFilters(prev => ({ ...prev, season: currentSeason.id }));
      } else if (futureSeasons.length > 0) {
        setCurrentSeasonId(futureSeasons[0].id);
        setFilters(prev => ({ ...prev, season: futureSeasons[0].id }));
      }
    } catch (err) {
      console.error('Failed to load seasons:', err);
      showError('Failed to load seasons');
      setSeasons([]);
    }
  }, [showError]);

  const loadTeams = useCallback(async () => {
    try {
      setLoading(true);

      const response = await apiService.getAllTeams({
        program_id: filters.program,
        status: filters.status,
        season_id: filters.season
      });
      setTeams(response.teams || []);
    } catch (err) {
      console.error('Failed to load teams:', err);
      showError('Failed to load teams');
    } finally {
      setLoading(false);
    }
  }, [filters.program, filters.status, filters.season, showError]);

  const loadPrograms = useCallback(async () => {
    try {
      const response = await apiService.request('/api/programs/');
      setPrograms(response.programs || []);
    } catch (err) {
      console.error('Failed to load programs:', err);
      setPrograms([]);
    }
  }, []);

  useEffect(() => {
    loadSeasons();
    loadPrograms();
  }, [loadSeasons, loadPrograms]);

  useEffect(() => {
    loadTeams();
  }, [loadTeams]);

  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  const handleDeleteTeam = async () => {
    if (!showDeleteConfirm) return;

    setIsDeleting(true);
    try {
      await apiService.deleteTeam(showDeleteConfirm.id);
      showSuccess(`Team "${showDeleteConfirm.name}" deleted successfully`);
      setShowDeleteConfirm(null);
      await loadTeams();
    } catch (err) {
      console.error('Error deleting team:', err);
      showError('Failed to delete team');
    } finally {
      setIsDeleting(false);
    }
  };

  const filteredTeams = teams.filter(team => {
    if (filters.search) {
      const searchTerm = filters.search.toLowerCase();
      if (!team.name?.toLowerCase().includes(searchTerm) &&
        !team.age_group_label?.toLowerCase().includes(searchTerm) &&
        !team.season?.toLowerCase().includes(searchTerm)) {
        return false;
      }
    }
    return true;
  });

  const getProgramName = (programId) => {
    const program = programs.find(p => p.id === programId);
    return program?.name || 'Unknown Program';
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'active':
      case 'full':
        return 'success';
      case 'inactive':
        return 'warning';
      default:
        return 'secondary';
    }
  };

  const copyRegistrationLink = async (registrationLink, teamId) => {
    try {
      await navigator.clipboard.writeText(registrationLink);
      setCopiedTeamId(teamId);
      showSuccess('Registration link copied to clipboard!');
      setTimeout(() => {
        setCopiedTeamId(null);
      }, 2000);
    } catch (err) {
      console.error('Failed to copy link:', err);
      const textArea = document.createElement('textarea');
      textArea.value = registrationLink;
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        setCopiedTeamId(teamId);
        showSuccess('Registration link copied to clipboard!');
        setTimeout(() => {
          setCopiedTeamId(null);
        }, 2000);
      } catch (fallbackErr) {
        console.error('Fallback copy failed:', fallbackErr);
      }
      document.body.removeChild(textArea);
    }
  };

  const filterBar = (
    <ListingFilter
      searchValue={filters.search}
      onSearchChange={(val) => handleFilterChange('search', val)}
      searchPlaceholder="Search teams by name, age group, or season..."
      filters={[
        {
          name: 'season',
          label: 'Season',
          options: [
            { value: '', label: 'All Seasons' },
            ...seasons.map(season => ({ value: season.id, label: season.name }))
          ]
        },
        {
          name: 'program',
          label: 'Program',
          options: [
            { value: '', label: 'All Programs' },
            ...programs.map(program => ({ value: program.id, label: program.name }))
          ]
        },
        {
          name: 'status',
          label: 'Status',
          options: [
            { value: '', label: 'All Statuses' },
            { value: 'active', label: 'Active' },
            { value: 'full', label: 'Full' },
            { value: 'inactive', label: 'Inactive' }
          ]
        }
      ]}
      activeFilters={{ season: filters.season, program: filters.program, status: filters.status }}
      onFilterChange={(name, val) => handleFilterChange(name, val)}
      onClearFilters={() => setFilters({ search: '', program: '', status: '', season: currentSeasonId || '' })}
    />
  );

  const headerActions = (
    <ListingActionBar
      rightSection={
        <CreateAction 
          onClick={() => navigate(branch_id ? `/branch/${branch_id}/teams/add` : "/admin/teams/add")}
          label="New Team"
        />
      }
    />
  );

  const renderTeamActions = (team) => {
    const actions = [];

    if (team.registration_link) {
      actions.push(
        <button
          key="copy"
          onClick={() => copyRegistrationLink(team.registration_link, team.id)}
          style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.8rem', color: 'var(--color-primary)', background: 'none', border: 'none', padding: '0' }}
          title="Copy registration link"
        >
          {copiedTeamId === team.id ? <Check size={14} /> : <Copy size={14} />}
        </button>
      );
    }

    actions.push(
      <button
        key="offers"
        onClick={() => navigate(branch_id ? `/branch/${branch_id}/teams/${team.id}/offers` : `/admin/teams/${team.id}/offers`)}
        style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.8rem', color: 'var(--color-primary)', background: 'none', border: 'none', padding: '0' }}
        title="Manage offers for this team"
      >
        <UserPlus size={14} />
      </button>
    );

    actions.push(
      <EditAction size="xs" key="edit" onClick={() => navigate(branch_id ? `/branch/${branch_id}/teams/${team.id}/edit` : `/admin/teams/${team.id}/edit`)} />
    );

    actions.push(
      <DeleteAction size="xs" key="delete" onClick={() => setShowDeleteConfirm(team)} />
    );

    return <div style={{ display: 'flex', gap: '8px' }}>{actions}</div>;
  };

  return (
    <AdminPageLayout
      title="TEAM MANAGEMENT"
      subtitle="View and manage teams across all programs"
      backPath="/admin/club"
      maxWidth="1400px"
    >
      <Section title="TEAMS">
        <Listing
          loading={loading}
          items={filteredTeams}
          actions={headerActions}
          filters={filterBar}
          variant="list"
          renderItem={(team) => (
            <ListingItem
              key={team.id}
              title={team.name}
              subtitle={team.age_group_description || team.age_group_label}
              icon={<Users size={32} />}
              iconVariant={getStatusColor(team.status)}
              actions={renderTeamActions(team)}
            >
              <div style={{ display: 'flex', gap: '16px', marginTop: '4px', flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.8rem', color: 'var(--color-text-tertiary)' }}>
                  {getProgramName(team.program_id)}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.8rem', color: 'var(--color-text-tertiary)' }}>
                  Season: {team.season || '-'}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.8rem', color: 'var(--color-text-tertiary)' }}>
                  Capacity: {team.current_enrollment || 0} / {team.max_capacity || 0}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.8rem' }}>
                  {team.status?.toLowerCase() === 'active' ? (
                    <span style={{ color: 'var(--color-success)', display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <CheckCircle size={14} /> Active
                    </span>
                  ) : team.status?.toLowerCase() === 'full' ? (
                    <span style={{ color: 'var(--color-success)', display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <CheckCircle size={14} /> Full
                    </span>
                  ) : (
                    <span style={{ color: 'var(--color-warning)', display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <XCircle size={14} /> Inactive
                    </span>
                  )}
                </div>
              </div>
            </ListingItem>
          )}
          emptyMessage="No teams found"
        />
      </Section>

      {showDeleteConfirm && (
        <ConfirmDialog
          isOpen={true}
          title="DELETE TEAM"
          message={`Are you sure you want to delete "${showDeleteConfirm.name}"?`}
          details="This action cannot be undone. All team data will be permanently removed."
          variant="danger"
          onConfirm={handleDeleteTeam}
          onClose={() => setShowDeleteConfirm(null)}
          confirmText="DELETE TEAM"
          cancelText="CANCEL"
          loading={isDeleting}
        />
      )}
    </AdminPageLayout>
  );
};

export default ManageTeams;
